import type { Brands } from "../brand/types";
import { createStorageClient } from "../integration/frontend-cmp-client";
import type {
  PageTracker,
  ThirdPartyTrackingAgentArgs,
} from "../integration/frontend-tracking-agent";
import {
  createCustomDimensionsRepository,
  createDatalayerTrackingClient,
  createDataMeshTrackingClient,
  createECommerceTrackingClient,
  createThirdPartyTrackingAgent,
  createTrackingParamsRepository,
  pageTracker,
} from "../integration/frontend-tracking-agent";
import type { MicroFrontEndConfig } from "../microFrontend/MFEPageProvider";
import type { StorageClient } from "../storage/types";
import type { CustomDimensionsRepository } from "../tracking/CustomDimensionsProvider";
import type { DatalayerTrackingClient } from "../tracking/DatalayerTrackingProvider";
import type { DataMeshTrackingClient } from "../tracking/DataMeshTrackingProvider";
import type { ECommerceTrackingClient } from "../tracking/ECommerceTrackingProvider";
import type { ThirdPartyTrackingAgent } from "../tracking/thirdPartyTrackingAgent";
import type { TrackingParamsRepository } from "../tracking/TrackingProvider";

export interface AppProviderCorePropsConfig {
  brandCode: Brands;
  locale: { i18n: string };
  trackingAgent: ThirdPartyTrackingAgentArgs;
  mfe?: MicroFrontEndConfig;
}

export interface AppProviderCoreProps {
  storageClient: StorageClient;
  pageTracker?: PageTracker;
  customDimensionsRepository: CustomDimensionsRepository;
  datalayerTrackingClient: DatalayerTrackingClient;
  eCommerceTrackingClient: ECommerceTrackingClient;
  trackingParamsRepository: TrackingParamsRepository;
  dataMeshTrackingClient: DataMeshTrackingClient;
  thirdPartyTrackingAgent: ThirdPartyTrackingAgent;
}

const EMPTY_MFE = undefined;

export const createAppProviderCoreProps = ({
  brandCode,
  locale,
  trackingAgent,
  mfe = EMPTY_MFE,
}: AppProviderCorePropsConfig): AppProviderCoreProps => {
  const { trackPage, trackSurvey } = trackingAgent;
  const storageClient = createStorageClient(brandCode, locale.i18n);
  const customDimensionsRepository = createCustomDimensionsRepository();
  const datalayerTrackingClient = createDatalayerTrackingClient({
    storageClient,
  });
  const eCommerceTrackingClient = createECommerceTrackingClient();
  const trackingParamsRepository = createTrackingParamsRepository();
  const dataMeshTrackingClient = createDataMeshTrackingClient({
    storageClient,
    mfe,
  });
  const thirdPartyTrackingAgent = createThirdPartyTrackingAgent({
    trackPage,
    trackSurvey,
    routeMatcher: {
      getPathParams: () => ({}),
      findMatchingPattern: () => undefined,
    },
    routePageMap: {},
  });

  return {
    storageClient,
    pageTracker,
    customDimensionsRepository,
    datalayerTrackingClient,
    eCommerceTrackingClient,
    trackingParamsRepository,
    dataMeshTrackingClient,
    thirdPartyTrackingAgent,
  };
};
